import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredToken } from '../../utils/tokenStorage';
import { formatDate } from '../../utils/dateFormat';
import ConfirmationModal from '../../components/common/ConfirmationModal';

interface PendingRequest {
  id: number;
  file_id: number;
  filename: string;
  molecule_count: number;
  requested_by_name: string | null;
  requested_by_email: string | null;
  requested_at: string | null;
}

interface ApprovedEntry {
  id: number;
  file_id: number;
  filename: string;
  molecule_count: number;
  pushed_by_name: string | null;
  pushed_at: string | null;
  is_locked: boolean;
}

const ReferenceLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingRequest[]>([]);
  const [approved, setApproved] = useState<ApprovedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; filename: string } | null>(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getStoredToken()}`,
  }), []);

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        fetch('/api/v1/admin/reference-library/pending', { headers: headers() }),
        fetch('/api/v1/reference-library', { headers: headers() }),
      ]);
      if (pendingRes.ok) setPending(await pendingRes.json());
      if (approvedRes.ok) setApproved(await approvedRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id: number) => {
    const res = await fetch(`/api/v1/admin/reference-library/${id}/approve`, {
      method: 'POST',
      headers: headers(),
    });
    if (res.ok) fetchData();
  };

  const handleReject = async (id: number) => {
    const res = await fetch(`/api/v1/admin/reference-library/${id}/reject`, {
      method: 'POST',
      headers: headers(),
    });
    if (res.ok) fetchData();
  };

  const handleRemove = async (id: number) => {
    const res = await fetch(`/api/v1/admin/reference-library/${id}`, {
      method: 'DELETE',
      headers: headers(),
    });
    if (res.ok) fetchData();
    setConfirmDelete(null);
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Pending Requests */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          Pending Requests
          {pending.length > 0 && (
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-amber-500 rounded-full">
              {pending.length}
            </span>
          )}
        </h2>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No pending requests.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">File</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Molecules</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Requested By</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Date</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {pending.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{req.filename}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{req.molecule_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {req.requested_by_name || req.requested_by_email || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {req.requested_at ? formatDate(req.requested_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        onClick={() => handleApprove(req.id)}
                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(req.id)}
                        className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Approved Files */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Reference Library ({approved.length} {approved.length === 1 ? 'file' : 'files'})
        </h2>
        {approved.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No files in the reference library yet. Members can submit files for inclusion from the file editor.
          </p>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">File</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Molecules</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Added By</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Date</th>
                  <th className="px-4 py-2 font-medium text-gray-600 dark:text-gray-300 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {approved.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 font-medium" style={{ color: '#0d9488', cursor: 'pointer' }}
                      onClick={() => navigate(`/chemistry/file/${entry.file_id}/editor`)}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >{entry.filename}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{entry.molecule_count.toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{entry.pushed_by_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {entry.pushed_at ? formatDate(entry.pushed_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setConfirmDelete({ id: entry.id, filename: entry.filename })}
                        className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 rounded transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <ConfirmationModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleRemove(confirmDelete.id)}
        title="Delete Reference File"
        description={`Are you sure you want to delete '${confirmDelete?.filename}'? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="red"
      />
    </div>
  );
};

export default ReferenceLibraryPage;
